import React, { useState } from 'react';
import { ArrowRight, Phone, MapPin, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { serviceTypes, footerLinks } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { createConsultation } from '../api/easyworks';
import { validateConsultation, hasErrors } from '../lib/validators';

const Enterprise = () => (
  <section className="bg-[#FAF2E8] py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="relative rounded-3xl bg-white border border-black/5 shadow-sm overflow-hidden p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h3 className="text-2xl md:text-4xl font-extrabold text-neutral-900 leading-tight">
            Personalised service for{' '}
            <span className="text-[#F39200]">large enterprise</span>
          </h3>
          <p className="mt-4 text-neutral-600 text-[15px] max-w-md">
            From Fortune 500 to fast-growing scale-ups — our enterprise team designs compliance solutions that fit your exact operations.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 mt-6 bg-neutral-900 hover:bg-neutral-800 text-white px-5 h-11 rounded-lg text-sm font-medium transition-colors">
            Get in touch <ArrowRight size={16} />
          </a>
        </div>
        <div className="relative h-60 md:h-72 rounded-2xl overflow-hidden bg-[#FFF5E8]">
          <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=900&q=80" alt="team" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
);

const ContactSection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ company: '', name: '', phone: '', email: '', service: 'VPOB & APOB', ecom: false, website: '' });
  const [errors, setErrors] = useState({});
  const [serviceOpen, setServiceOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateConsultation(form);
    if (hasErrors(found)) {
      setErrors(found);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await createConsultation({
        company: form.company,
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service,
        isEcommerce: form.ecom,
        source: 'contact',
        website: form.website,
      });
      toast({ title: 'Thanks for reaching out!', description: 'Our team will contact you within 24 hours.' });
      setForm({ company: '', name: '', phone: '', email: '', service: 'VPOB & APOB', ecom: false, website: '' });
      setErrors({});
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.';
      toast({ title: 'Submission failed', description: typeof msg === 'string' ? msg : 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0F0F10] text-white py-20 relative overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-3xl opacity-30" style={{ background: 'radial-gradient(closest-side, #F39200, transparent 70%)' }} />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">
            Get in touch with us for a{' '}
            <span className="text-[#F39200]">Free Consultation</span> call!
          </h2>
          <p className="mt-5 text-neutral-300 max-w-md text-[15px]">
            Get a personalised quote for all your compliance needs. From Startups compliance to Fortune 500 legals we cover it all.
          </p>
          <div className="mt-8 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F39200]/15 text-[#F39200] flex items-center justify-center"><Phone size={18} /></div>
              <div>
                <div className="text-xs text-neutral-400">Phone:</div>
                <div className="text-sm font-medium">+91 9717174320</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#F39200]/15 text-[#F39200] flex items-center justify-center"><MapPin size={18} /></div>
              <div>
                <div className="text-xs text-neutral-400">Address:</div>
                <div className="text-sm font-medium max-w-xs">C-110, 1st and 4th Floor, Sunidhi Complex, Ashok Vihar Ph-2, Gurugram, Haryana - 122017</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="text-lg font-semibold">Get your <span className="text-[#F39200]">free consultation</span> by expert</div>
          <div className="mt-5 space-y-3">
            {/* Honeypot */}
            <input type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true"
              value={form.website} onChange={(e)=>update('website', e.target.value)}
              style={{ position:'absolute', left:'-10000px', width:'1px', height:'1px', opacity:0 }} />
            <input value={form.company} onChange={(e)=>update('company', e.target.value)} data-testid="contact-input-company" className="w-full h-11 px-3.5 rounded-lg bg-white/[0.06] border border-white/10 focus:border-[#F39200] outline-none text-sm placeholder:text-neutral-400" placeholder="Company name" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  value={form.name}
                  onChange={(e)=>update('name', e.target.value)}
                  aria-invalid={!!errors.name}
                  data-testid="contact-input-name"
                  className={`w-full h-11 px-3.5 rounded-lg bg-white/[0.06] border outline-none text-sm placeholder:text-neutral-400 ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#F39200]'}`}
                  placeholder="Your name *"
                />
                {errors.name && <p data-testid="contact-error-name" className="mt-1 text-xs text-red-400">{errors.name}</p>}
              </div>
              <div>
                <input
                  value={form.phone}
                  onChange={(e)=>update('phone', e.target.value)}
                  aria-invalid={!!errors.phone}
                  data-testid="contact-input-phone"
                  className={`w-full h-11 px-3.5 rounded-lg bg-white/[0.06] border outline-none text-sm placeholder:text-neutral-400 ${errors.phone ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#F39200]'}`}
                  placeholder="Phone Number *"
                />
                {errors.phone && <p data-testid="contact-error-phone" className="mt-1 text-xs text-red-400">{errors.phone}</p>}
              </div>
            </div>
            <div>
              <input
                value={form.email}
                onChange={(e)=>update('email', e.target.value)}
                aria-invalid={!!errors.email}
                data-testid="contact-input-email"
                className={`w-full h-11 px-3.5 rounded-lg bg-white/[0.06] border outline-none text-sm placeholder:text-neutral-400 ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#F39200]'}`}
                placeholder="Email ID"
              />
              {errors.email && <p data-testid="contact-error-email" className="mt-1 text-xs text-red-400">{errors.email}</p>}
            </div>
            <div className="relative">
              <button type="button" onClick={()=>setServiceOpen(!serviceOpen)} className="w-full h-11 px-3.5 rounded-lg bg-white/[0.06] border border-white/10 hover:border-[#F39200] flex items-center justify-between text-sm text-neutral-100">
                {form.service}
                <ChevronDown size={16} className={`transition-transform ${serviceOpen ? 'rotate-180' : ''}`} />
              </button>
              {serviceOpen && (
                <ul className="absolute z-30 mt-1 w-full bg-neutral-900 border border-white/10 rounded-lg shadow-lg overflow-hidden">
                  {serviceTypes.map((s) => (
                    <li key={s}>
                      <button type="button" onClick={()=>{ setForm({...form, service:s}); setServiceOpen(false); }} className="w-full text-left px-3.5 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]">{s}</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer w-fit">
              <input type="checkbox" checked={form.ecom} onChange={(e)=>setForm({...form, ecom:e.target.checked})} className="accent-[#F39200] w-4 h-4" />
              Are you an E-Commerce seller?
            </label>
            <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="w-full h-11 mt-1 rounded-lg bg-[#F39200] hover:bg-[#E08400] disabled:opacity-60 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
              {submitting ? 'Submitting...' : (<>Contact Us Now <ArrowRight size={16} /></>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#0A0A0B] text-neutral-300 pt-14 pb-8">
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
      <div>
        <div className="flex items-center gap-1">
          <span className="font-extrabold text-2xl" style={{ color: '#F39200' }}>easy</span>
          <span className="font-extrabold text-2xl text-white">works</span>
        </div>
        <p className="mt-4 text-sm text-neutral-400 max-w-xs leading-relaxed">
          One-stop solution for VPOB, APOB, GST advisory and complete startup compliance across India.
        </p>
      </div>
      <div>
        <div className="text-white font-semibold mb-4">Quick links</div>
        <ul className="space-y-2 text-sm">
          {footerLinks.quick.map((l) => (
            <li key={l.label}><Link to={l.href} className="hover:text-[#F39200] transition-colors">{l.label}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-white font-semibold mb-4">GST Advisory</div>
        <ul className="space-y-2 text-sm">
          {footerLinks.gst.map((l) => (
            <li key={l.label}><Link to={l.href} className="hover:text-[#F39200] transition-colors">{l.label}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-white font-semibold mb-4">VPOB & APOB by States</div>
        <ul className="space-y-2 text-sm">
          {footerLinks.states.map((l) => (
            <li key={l.label}><Link to={l.href} className="hover:text-[#F39200] transition-colors">{l.label}</Link></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 lg:px-10 mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="text-xs text-neutral-500">© {new Date().getFullYear()} Easyworks. All rights reserved.</div>
      <div className="flex flex-wrap gap-5 text-xs">
        {footerLinks.legal.map((l) => (
          <Link key={l.label} to={l.href} data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+/g,'-')}`} className="text-neutral-400 hover:text-[#F39200] transition-colors">{l.label}</Link>
        ))}
      </div>
    </div>
  </footer>
);

export { Enterprise, ContactSection, Footer };
